import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
} from '@angular/core';

import { QuickSwitchItem } from '../insight/config.insight.model';
import { CacheDateItem } from '../performance-data/cache-date.pd.model';
import { disuseStore, useStore } from '../store.decorator';
import { PeriodType } from '../widget/date-range.widget.model';

import { TranslationKeyMap } from './human-insight.i18n';
import { SuperGroupState } from './human-insight.state';
import {
    SuperGroupExtra,
    SuperGroupHumanInsightSelectionChangeEvent,
} from './super-group.hi.model';

interface DateRange {
    key: number;
    start: Date;
    end: Date;
    items: QuickSwitchItem[];
}

function deDup(items: QuickSwitchItem[]): QuickSwitchItem[] {
    // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
    return (
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        items &&
        items
            .reduce<QuickSwitchItem[]>((p, c) => {
                if (
                    p.findIndex(
                        (e) => e.initialCriteria === c.initialCriteria,
                    ) === -1
                ) {
                    p.push(c);
                }
                return p;
            }, [])
            .filter((e) => !!e)
    );
}

@Component({
    selector: '[cmpsHumanInsightSuperGroup]',
    templateUrl: './super-group.hi.component.html',
})
export class SuperGroupHumanInsightComponent
    implements OnChanges, OnInit, OnDestroy
{
    state: SuperGroupState;

    @Input() cacheDates: CacheDateItem[];
    @Input() eventDate: Date;
    @Input() selected: QuickSwitchItem;
    @Output()
    selectionChange: EventEmitter<SuperGroupHumanInsightSelectionChangeEvent>;

    localState: { dateRange?: CacheDateItem };

    /** @private */
    _i18n = TranslationKeyMap;

    /** @private */
    _PeriodType = PeriodType;

    /** @private */
    _superGroupItemList: Array<QuickSwitchItem<SuperGroupExtra>>;

    /** @private */
    _previousSuperGroupList: Array<QuickSwitchItem<SuperGroupExtra>>;

    /** @private */
    _personalDates: DateRange[];

    /** @private */
    _teamDates: DateRange[];

    constructor() {
        this.selectionChange = new EventEmitter();

        this.localState = {};
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (
            changes.cacheDates != null ||
            changes.eventDate != null ||
            changes.selected != null
        ) {
            this._setValues();
        }
    }

    ngOnInit(): void {
        useStore(this, 'humanInsight.superGroup');
        this._setValues();
    }

    ngOnDestroy(): void {
        disuseStore(this);
        this.selectionChange.complete();
    }

    propsToDispatch(): void {
        if (this.cacheDates != null && this.eventDate != null) {
            const dateRange = this.cacheDates.find(
                (c) =>
                    c.eventDate &&
                    c.eventDate.getTime() === this.eventDate.getTime(),
            );
            this.localState = { ...this.localState, dateRange };
        }
    }

    /** @private */
    _onSelectionChange(item: QuickSwitchItem<SuperGroupExtra>): void {
        const orig =
            item &&
            this.state?.filteredItems.find(
                (e) => e.initialCriteria === item.initialCriteria,
            );
        if (!orig || this.selected === orig) return;
        this.selectionChange.emit({ selected: orig });
    }

    private _filterRecent(): Array<QuickSwitchItem<SuperGroupExtra>> {
        const items = this.state?.filteredItems || [];
        const nonTeam = items
            .filter((e) => !e.extra.teamSuperGroup)
            .reduce((p, c) => {
                return !p ||
                    (c.dateRanges &&
                        c.dateRanges.length !== 0 &&
                        c.dateRanges.some((e) => {
                            return (
                                p.dateRanges &&
                                p.dateRanges.length !== 0 &&
                                e.end >=
                                    p.dateRanges.reduce(
                                        (pp, cc) =>
                                            !pp || cc.end > pp.end ? cc : pp,
                                        null,
                                    ).end
                            );
                        }))
                    ? c
                    : p;
            }, null);
        const team = items.filter((e) => e.extra.teamSuperGroup); // dates won't be shown
        return !nonTeam ? deDup(team) : deDup([nonTeam, ...team]);
    }

    private _filterPrevious(recent: QuickSwitchItem[]): QuickSwitchItem[] {
        const recentDateRanges = recent.reduce((p, c) => {
            if (c.dateRanges) {
                p.push(...c.dateRanges);
            }
            return p;
        }, []);
        const items = (this.state?.filteredItems || [])
            .map((e) => {
                // eslint-disable-next-line @typescript-eslint/prefer-includes
                const dateRanges = e.dateRanges.filter(
                    // eslint-disable-next-line @typescript-eslint/prefer-includes
                    (ee) => recentDateRanges.indexOf(ee) === -1,
                );
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                return { ...e, dateRanges } as QuickSwitchItem<SuperGroupExtra>;
            })
            .filter((e) => e.dateRanges && e.dateRanges.length !== 0)
            .sort((a) => (a.extra.teamSuperGroup ? 0 : 1));
        return deDup(items);
    }

    private _setValues(): void {
        this._superGroupItemList = this._filterRecent();
        this._previousSuperGroupList = this._filterPrevious(
            this._superGroupItemList,
        );

        const pivot = (items2: QuickSwitchItem[]): DateRange[] => {
            const piv: DateRange[] = [];
            items2.forEach((e) => {
                e.dateRanges.forEach((ee) => {
                    const key = ee.start.getTime() + ee.end.getTime();
                    const index = piv.findIndex((eee) => eee.key === key);
                    if (index === -1) {
                        piv.push({
                            key,
                            start: ee.start,
                            end: ee.end,
                            items: [e],
                        });
                    } else {
                        piv[index].items.push(e);
                    }
                });
            });
            return piv.sort((a, b) => a.end.getTime() - b.end.getTime());
        };
        this._personalDates = pivot(
            this._previousSuperGroupList.filter((e) => !e.extra.teamSuperGroup),
        );
        this._teamDates = pivot(
            this._previousSuperGroupList.filter((e) => e.extra.teamSuperGroup),
        );
    }
}
